'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import useTapestryState from '../hooks/useTapestryState';
import useTapestryLayout from '../hooks/useTapestryLayout';
import useTapestryReplay from '../hooks/useTapestryReplay';
import useCanvasSize from '../hooks/useCanvasSize';
import { CANVAS_ASPECT } from '../lib/tapestryPacker';
import { createSymbolImageClient } from '../lib/symbolImageClient';
import { fullscreenAsideWidth as asideWidth } from '../lib/fullscreenAside';
import { createSymbolKeywordIndex } from '../lib/symbolMeta';
import { prepareLayoutCatalog } from '../lib/selectLayout';
import { TapestrySkinProvider, resolveSkin } from '../lib/tapestrySkin';
import { fillSlots, replayFill } from '../lib/fillSlots';
import { buildCuratedComposition } from '../lib/curatedLayout';
import { paintFrame, paintSignature } from '../lib/paint';
import TapestrySymbol from './TapestrySymbol';
import TapestryExternal from './TapestryExternal';
import CuratedText from './CuratedText';
import LayoutSnackbar from './LayoutSnackbar';
import BackgroundReveal, { FADE_DURATION_MS, FILL_LAYER_STYLE } from './BackgroundReveal';
import { TapestryStaggerProvider } from '../lib/tapestryStagger';
import { SeenRevealProvider } from '../lib/seenReveal';
import { exportTapestryPng } from '../lib/exportImage';
import '../styles/tapestryAnimations.css';

// Reveal window: how long a background/piece takes to become fully opaque.
// Feeds the floor timing (when the floor advances to the current background).
// Equals the fade's own visual length so the two never disagree (TT-975).
const REVEAL_DURATION_MS = FADE_DURATION_MS;

// Progressive multi-piece backgrounds: how often the next piece begins fading
// in. Still shorter than the fade, so consecutive pieces overlap and the reveal
// reads as continuous rather than one-at-a-time with gaps — but only just
// (TT-1008 held the overlap at its old 500ms while stretching the fade, so it
// shrank from a third of the fade to ~22%). Tune to taste.
const PIECE_INTERVAL_MS = 1750;
const EMPTY_PIECES = [];
const EMPTY_PLACEMENTS = [];

/**
 * Tapestry canvas — the root Live Tap renderer component. Generative
 * visualization of a live meeting's scored segments.
 *
 * This is the public entry point of the vendored renderer; it is
 * backend-agnostic and reaches symbol data only through the injected
 * `resources` adapter. See ../README.md for the full renderer contract.
 *
 * Props:
 *   - segments, currentSent, threshold — scored meeting data + playhead. The
 *     playhead is a SENTENCE ordinal, not a turn index (TT-971): a live meeting
 *     grows inside its last turn, so only a sentence can name a position in it.
 *     Turn-stepping hosts convert with `turnEndSentence` (lib/streamSegments).
 *   - meetingName  — title shown top-left.
 *   - meetingDate  — preformatted date string shown under the title (the host
 *                    app fetches/formats it; the renderer does no backend I/O).
 *   - resources    — { loadMasterSymbols(), loadSymbols(), loadSymbolImages(symbolId) } adapter.
 *   - debugTools   — show the debug toggle + panel under the canvas
 *                    (default true; hosts can pass false for a clean view).
 *   - fullscreenControl — show the canvas's own fullscreen button (default
 *                    true). A host that wraps the canvas in fullscreen chrome
 *                    of its own (tapp FullscreenStage over the scene opening,
 *                    TT-1133) passes false, or two buttons stack top-right.
 *   - fullscreenRef — optional separate control ref, exposing `toggle()` for
 *                    the same fullscreen state as the canvas's own button.
 *
 * Manual background selection (TT-962) — optional, backward-compatible. When a
 * host wants to drive the manual background pin from its own chrome (a palette
 * button + picker modal), it can control it:
 *   - forcedBackgroundId          — controlled pin (a background id, or null for
 *                                   automatic). Omit (undefined) to leave the pin
 *                                   uncontrolled, i.e. the built-in Ctrl/Cmd+Shift
 *                                   hotkeys own it internally as before.
 *   - onForcedBackgroundIdChange  — called with the new id (or null) whenever the
 *                                   pin changes, including from the hotkeys, so a
 *                                   controlling host stays in sync. Pass a stable
 *                                   fn (e.g. a useState setter).
 *   - onEligibleBackgroundsChange — called with the eligible (selected + published
 *                                   + renderable-layout) background catalog once it
 *                                   loads, so a host can render a contact sheet of
 *                                   exactly the backgrounds a pin can display. Pass
 *                                   a stable fn.
 *
 * Active background (TT-972) — optional:
 *   - onActiveBackgroundChange    — called with `{ id, primarySymbolId }` for the
 *                                   background the canvas is actually painting
 *                                   (automatic pick or manual pin, whichever won),
 *                                   or null while the view is still generative.
 *                                   Lets a host show that symbol's field-guide
 *                                   description beside the canvas. Pass a stable fn.
 *
 * The picture as a record (TT-1227) — optional:
 *   - onPaint                     — called with a frame (lib/paint.js) each time
 *                                   what the canvas paints changes: the phase,
 *                                   the background, the placements or the filled
 *                                   slots, the canvas size. A host records these.
 *   - playback                    — a frame to paint instead of the segments'
 *                                   picture: its background is loaded, its
 *                                   placements or slots painted as recorded.
 *                                   `onPaint` is quiet while a frame plays back.
 *
 * Imperative handle (via ref): `{ exportPng() }` — downloads the rendered
 * tapestry as a PNG. The host owns the button placement/chrome and calls this;
 * it resolves when the download has been triggered (errors are logged, not
 * thrown, so the caller can manage a loading flag with a plain try/finally).
 *
 * Fullscreen mode visually scales the existing canvas up via CSS transform,
 * without re-running the packer or recomputing symbol positions. The DOM
 * width/height stay frozen while fullscreen is active, so ResizeObserver
 * never fires and nothing reflows — only the visible scale changes.
 *
 * Fullscreen aside (TT-972) — optional, for hosts that show chrome beside a
 * fullscreen tapestry:
 *   - fullscreenAside        — a node to render in a gutter at the right edge while
 *                              fullscreen. Supplying one left-justifies the canvas
 *                              and refits it to the reduced width; omitting it (the
 *                              default) centers across the whole viewport exactly as
 *                              before. The renderer owns the gutter's geometry and
 *                              stacking — the host supplies only content, which
 *                              should fill the box (it is laid out as a flex row)
 *                              and paint its own surface, since the fullscreen
 *                              backdrop behind it is near-black.
 *   - onFullscreenChange     — called with the boolean fullscreen state, so a host
 *                              can drop its own inline placement of that chrome
 *                              while the aside is showing. Pass a stable fn.
 * The renderer decides how much room it gives up and where that room is; it never
 * learns what goes in it.
 */

const ROLE_STYLES = {
  primary: 'text-theme-accent-primary border-theme-accent-primary/40 bg-theme-accent-primary/10',
  secondary: 'text-amber-400 border-amber-400/30 bg-amber-400/5',
  tertiary: 'text-theme-secondary border-theme-border bg-theme-tertiary/40',
  hidden: 'text-theme-muted border-theme-border bg-transparent opacity-50',
};

const EVENT_STYLES = {
  'symbol.introduce': 'text-emerald-400',
  'symbol.reinforce': 'text-theme-secondary',
  'sentence.surface': 'text-theme-muted',
  'primary.change': 'text-theme-accent-primary font-semibold',
  'layout.change': 'text-sky-400 font-semibold',
};

const CANVAS_BG = {
  backgroundImage: 'url(/tapestry/background.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

// How long a removed placement lingers in the DOM with opacity fading
// to 0. Anything shorter feels abrupt; longer makes scrubbing soupy.
const EXIT_FADE_MS = 600;

// Manual background override: Ctrl/Cmd+Shift+1..9 pins one of these backgrounds
// (by id); Ctrl/Cmd+Shift+0 resets to automatic selection. Each must be a
// renderable background (active layout with slots) or pinning stays generative.
const MANUAL_BACKGROUND_IDS = [
  '67fe7e0e078151cf32c16fb3', // 1 - sea Turtle (progressive pieces)
  '696be16ccb83b23aee6b2f6a', // 2 - Profile w Brain (progressive pieces)
  '673ce8016776ea3bb6d1c48c', // 3 (was 5)
  '67b7533f340f4da72ec16cea', // 4 (was 3)
  '67bb74ead45e7fb6aa8e39e3', // 5 (was 4)
  '6818dab43dc5a3ddeaa60196', // 6
  '64518b3619e66d67f3fbc301', // 7
  '685dadea43d3c09e8a44ad18', // 8
  '67e1d5177027fa9efa3d9d36', // 9
];

/**
 * Retain dropped placements briefly so they can fade out instead of
 * popping. Returns a list that is the union of incoming placements
 * (marked `exiting: false`) and recently-removed ones (marked
 * `exiting: true, exitedAt: <timestamp>`). A periodic sweep prunes
 * exits whose fade window has elapsed.
 */
function useExitFadePlacements(placements, instant) {
  const [combined, setCombined] = useState([]);

  const [combinedFor, setCombinedFor] = useState(null);
  if (placements !== combinedFor) {
    setCombinedFor(placements);
    setCombined((prev) => {
      const incomingKeys = new Set(placements.map((p) => p.symbolId));
      const fresh = placements.map((p) => ({ ...p, exiting: false }));
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const carriedExits = [];
      for (const item of prev) {
        const key = item.symbolId;
        if (incomingKeys.has(key)) continue;
        if (item.exiting) {
          if (now - item.exitedAt < EXIT_FADE_MS) carriedExits.push(item);
        } else {
          carriedExits.push({ ...item, exiting: true, exitedAt: now });
        }
      }
      return [...fresh, ...carriedExits];
    });
  }

  useEffect(() => {
    const id = setInterval(() => {
      setCombined((curr) => {
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const next = curr.filter((c) => !c.exiting || now - c.exitedAt < EXIT_FADE_MS);
        return next.length === curr.length ? curr : next;
      });
    }, 100);
    return () => clearInterval(id);
  }, []);

  return instant ? placements : combined;
}

// Preloaded once on first TapestryCanvas mount so the first chalk reveal
// doesn't flicker while the browser fetches the sprites.
const REVEAL_ASSETS = [
  '/tapestry/pen.jpg',
  '/tapestry/chalk-burst.jpg',
  '/tapestry/chalk-wisp.jpg',
  '/tapestry/chalk-scatter-1.jpg',
  '/tapestry/chalk-scatter-2.jpg',
];
let revealAssetsPreloaded = false;
function preloadRevealAssets() {
  if (revealAssetsPreloaded || typeof Image === 'undefined') return;
  revealAssetsPreloaded = true;
  for (const src of REVEAL_ASSETS) {
    const img = new Image();
    img.src = src;
  }
}

function TapestryCanvas(
  {
    segments,
    currentSent,
    timeline = null,
    time = null,
    instant = false,
    threshold,
    meetingName,
    meetingDate,
    resources,
    debugTools = true,
    fullscreenControl = true,
    fullscreenRef,
    forcedBackgroundId: forcedBackgroundIdProp,
    onForcedBackgroundIdChange,
    onEligibleBackgroundsChange,
    onActiveBackgroundChange,
    fullscreenAside = null,
    onFullscreenChange,
    // 'chalkboard' (classic) or 'paper' (the scene system's opening — TT-1133).
    skin = 'chalkboard',
    // false: never load the layout catalog — the canvas stays generative
    // (symbols and sentences) for its whole life, whatever the adapter offers.
    layouts = true,
    // A host's own symbol-image client, to share its cache with a sibling
    // canvas; built from `resources` when absent.
    imageClient: imageClientProp = null,
    // A click on the canvas, as a point on it: `{ x, y, symbolId }`, x and y
    // fractions of the frame from its top-left, `symbolId` the symbol drawn
    // there (null on open canvas). What a point is for is the
    // host's (tapfly: a review note on that spot, TT-1211).
    onPoint,
    pointCursor = 'crosshair',
    // Host markers over the canvas: [{ id, x, y, symbolId?, label?, tone? }] in
    // the same fractions. A pin that names a symbol sits on that symbol
    // wherever it is drawn now, and at its x/y when it is not. Never exported.
    pins = null,
    onPinClick,
    // What is on the canvas, as the decisions behind it, each time that
    // changes (lib/paint.js): a host records these (tapfly: the review
    // cassette, TT-1227). Not called while painting a `playback` frame.
    onPaint,
    // A recorded frame to paint instead of reducing the segments: the
    // placements or the filled slots it names, on its background. The
    // segments still feed the transcript and the seen-reveal; the picture
    // is the frame's.
    playback = null,
  },
  ref
) {
  const skinCfg = useMemo(() => resolveSkin(skin), [skin]);
  // The canvas surface and the text layer's inks (styles/tapestryAnimations.css).
  const surface = useMemo(
    () => ({
      ...(skinCfg.surface ? { background: skinCfg.surface } : CANVAS_BG),
      '--tapestry-text-final': skinCfg.ink,
      '--tapestry-text-new': skinCfg.newInk,
    }),
    [skinCfg]
  );
  // Curated layout/background catalog, loaded once per adapter. Hosts whose
  // adapter predates the layout feature (no loadBackgrounds/loadLayouts) leave
  // this null, and the renderer stays in generative-only mode.
  const [layoutCatalog, setLayoutCatalog] = useState(null);
  useEffect(() => {
    if (!layouts || !resources || typeof resources.loadBackgrounds !== 'function' || typeof resources.loadLayouts !== 'function') {
      setLayoutCatalog(null);
      return;
    }
    let cancelled = false;
    const loadPrimary =
      typeof resources.loadPrimarySymbols === 'function' ? resources.loadPrimarySymbols() : Promise.resolve([]);
    Promise.all([resources.loadBackgrounds(), resources.loadLayouts(), loadPrimary])
      .then(([backgrounds, layouts, primarySymbolIds]) => {
        if (!cancelled) setLayoutCatalog(prepareLayoutCatalog(backgrounds, layouts, primarySymbolIds));
      })
      .catch((err) => {
        console.error('[TapestryCanvas] layout catalog load failed:', err);
        if (!cancelled) setLayoutCatalog(null);
      });
    return () => {
      cancelled = true;
    };
  }, [resources, layouts]);

  // Stable per-meeting seed for the no-symbol-match default background, so the
  // fallback varies across meetings but stays reproducible while scrubbing.
  const layoutSeed = useMemo(() => `${meetingName || ''}|${meetingDate || ''}`, [meetingName, meetingDate]);
  const state = useTapestryState(segments, currentSent, threshold, layoutCatalog, layoutSeed);
  const { symbols, primary, ranked, layout, events } = state;

  // Resource-bound clients — built once per adapter so the renderer reaches
  // symbols only through the injected `resources` contract, never a backend
  // directly. Memoized on `resources` so a stable adapter keeps its caches.
  const ownClient = useMemo(() => (imageClientProp ? null : createSymbolImageClient(resources)), [resources, imageClientProp]);
  const imageClient = imageClientProp || ownClient;
  const keywordIndex = useMemo(() => createSymbolKeywordIndex(resources), [resources]);

  // Master-symbol tags map (for the curated layout's `tags` slot rules), the
  // per-symbol library keyword sets (single-word lemmas for low-capacity slots)
  // and the catalog's names, for what the canvas says about a symbol.
  const [symbolTags, setSymbolTags] = useState(null);
  const [symbolKeywords, setSymbolKeywords] = useState(null);
  const [symbolNames, setSymbolNames] = useState(null);
  useEffect(() => {
    let cancelled = false;
    keywordIndex.loadSymbolTags().then((m) => {
      if (!cancelled) setSymbolTags(m);
    });
    keywordIndex.loadSymbolKeywords().then((m) => {
      if (!cancelled) setSymbolKeywords(m);
    });
    keywordIndex.loadSymbolNames().then((m) => {
      if (!cancelled) setSymbolNames(m);
    });
    return () => {
      cancelled = true;
    };
  }, [keywordIndex]);
  // A symbol as the canvas names it: its catalog name; its id until the
  // names land, or for art the catalog does not hold.
  const nameOf = (symbolId) => symbolNames?.get(symbolId) ?? symbolId;

  // Manual override (Ctrl/Cmd+Shift+1..9, or the host's picker modal). When set,
  // the renderer pins this background and ignores automatic selection until reset
  // (Ctrl/Cmd+Shift+0 / the modal's "auto" button). The pin is controlled when the
  // host passes `forcedBackgroundId` (it owns the state so its palette button/modal
  // can read + write it); otherwise it's internal and the hotkeys own it. Either
  // way, changes flow out through `onForcedBackgroundIdChange` so a host stays in
  // sync with the hotkeys.
  const [internalForcedBackgroundId, setInternalForcedBackgroundId] = useState(null);
  const isForcedBgControlled = forcedBackgroundIdProp !== undefined;
  const forcedBackgroundId = isForcedBgControlled ? forcedBackgroundIdProp : internalForcedBackgroundId;
  const applyForcedBackground = useCallback(
    (id) => {
      const next = id || null;
      if (!isForcedBgControlled) setInternalForcedBackgroundId(next);
      onForcedBackgroundIdChange?.(next);
    },
    [isForcedBgControlled, onForcedBackgroundIdChange]
  );
  // Latest applier held in a ref so the keydown listener (registered once) always
  // calls the current one without re-subscribing on every render.
  const applyForcedBackgroundRef = useRef(applyForcedBackground);
  applyForcedBackgroundRef.current = applyForcedBackground;

  // Once a layout/background clears hysteresis, fetch its resolved detail
  // (slots + rules) so we can fill and render the curated composition. A manual
  // override takes precedence over the reducer's committed layout.
  //
  // Hardened against transient failures: a failed/empty fetch is retried with
  // backoff instead of dropping to null (which would strand the view on the
  // generative layout). While retrying we keep any detail we already have, so a
  // blip never visibly reverts the background.
  // A playback frame names its own background (or none); the reducer's
  // committed layout and the manual override are the live picture's.
  const effectiveBgId = playback
    ? playback.background?.id || null
    : forcedBackgroundId || (layout ? layout.backgroundId : null);
  const [curatedDetail, setCuratedDetail] = useState(null);
  useEffect(() => {
    if (!effectiveBgId || !resources || typeof resources.loadBackgroundDetail !== 'function') {
      setCuratedDetail(null);
      return;
    }
    let cancelled = false;
    let timer = null;
    let attempt = 0;
    const MAX_ATTEMPTS = 5;

    const retry = () => {
      attempt += 1;
      if (cancelled || attempt >= MAX_ATTEMPTS) return; // give up; keep prior detail
      timer = setTimeout(load, Math.min(8000, 500 * 2 ** (attempt - 1)));
    };

    const load = () => {
      resources
        .loadBackgroundDetail(effectiveBgId)
        .then((detail) => {
          if (cancelled) return;
          // Only accept a usable detail (has renderable slots); otherwise retry.
          if (detail && detail.layout && (detail.layout.slots || []).length > 0) {
            setCuratedDetail(detail);
          } else {
            retry();
          }
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('[TapestryCanvas] background detail load failed:', err);
          retry();
        });
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [effectiveBgId, resources]);

  const curated = !!(effectiveBgId && curatedDetail?.id === effectiveBgId);
  // Curated layouts are authored in the background's aspect ratio (1/heightRatio);
  // the canvas adopts it so symbols + textpaths overlay the background exactly.
  // The generative phase keeps the default 16/9.
  const activeAspect = curated && curatedDetail.heightRatio ? 1 / curatedDetail.heightRatio : CANVAS_ASPECT;

  const [showDebug, setShowDebug] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useImperativeHandle(fullscreenRef, () => ({ toggle: () => setIsFullscreen((value) => !value) }), []);
  const canvasRef = useRef(null);
  const [canvasSize, snapLayout] = useCanvasSize(canvasRef, activeAspect, isFullscreen);
  const [windowSize, setWindowSize] = useState({ w: 0, h: 0 });
  const dateStr = meetingDate || '';
  // Mirror tapgen's title-date dedup (Tapestry.js): don't show the date when the
  // meeting name already contains it — as the full string or just its month/day
  // prefix — so it doesn't appear twice in the title.
  const nameStr = meetingName || '';
  const showDate = !!dateStr && !nameStr.includes(dateStr) && !nameStr.includes(dateStr.slice(0, 5));

  // Warm the reveal sprite cache on first mount
  useEffect(() => {
    preloadRevealAssets();
  }, []);

  // Track viewport dimensions while fullscreen so we can recompute scale on resize
  useEffect(() => {
    if (!isFullscreen) return;
    const update = () => setWindowSize({ w: window.innerWidth, h: window.innerHeight });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isFullscreen]);

  // ESC to exit fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  // Manual background override: Ctrl/Cmd+Shift+1..9 pins a background, +0 resets.
  // Match on e.code (Digit0..Digit9) since Shift mangles e.key into symbols.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || !e.shiftKey) return;
      const m = /^Digit([0-9])$/.exec(e.code);
      if (!m) return;
      e.preventDefault();
      const n = Number(m[1]);
      applyForcedBackgroundRef.current(n === 0 ? null : MANUAL_BACKGROUND_IDS[n - 1] || null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Publish the eligible (selected + published + renderable-layout) background
  // catalog to a controlling host so it can render a picker contact sheet of
  // exactly the backgrounds a manual pin can display. Fires once the catalog
  // loads and whenever it changes; empty until then / when the host adapter
  // predates the layout feature.
  useEffect(() => {
    onEligibleBackgroundsChange?.(layoutCatalog ? layoutCatalog.backgrounds : []);
  }, [layoutCatalog, onEligibleBackgroundsChange]);

  // Log automatic background picks NOT based on the primary symbol: a non-primary
  // (symbols-list) match or the seeded default. Only primary-symbol picks get the
  // snackbar; manual overrides are handled above.
  const loggedBgRef = useRef(null);
  useEffect(() => {
    if (forcedBackgroundId) return;
    const bg = layout?.backgroundId;
    if (!bg || layout?.primaryMatch || bg === loggedBgRef.current) return;
    loggedBgRef.current = bg;
    if (layout?.matchedSymbolId) {
      console.log(`[TapestryCanvas] background ${bg} selected via non-primary symbol "${layout.matchedSymbolId}"`);
    } else {
      console.log(`[TapestryCanvas] background ${bg} selected as default (no symbol match)`);
    }
  }, [layout?.backgroundId, layout?.primaryMatch, layout?.matchedSymbolId, forcedBackgroundId]);

  // Measured in the skin's face — the one TapestryExternal sets the lines in.
  const livePlacements = useTapestryLayout(state, canvasSize.height, keywordIndex, skinCfg.font, !timeline);
  const replayed = useTapestryReplay(timeline, time, {
    threshold, catalog: layoutCatalog, seed: layoutSeed,
    canvasHeight: canvasSize.width / CANVAS_ASPECT,
    keywords: symbolKeywords, tags: symbolTags, fontFamily: skinCfg.font, forcedBackgroundId,
  }, curated ? curatedDetail : null);
  const generativePlacements = timeline ? replayed?.placements || EMPTY_PLACEMENTS : livePlacements;

  // Freeze the on-screen sentences at the moment the curated layout commits, so
  // fillSlots favors what was visible during the generative phase (the smooth-
  // transition lever) and ongoing fills don't jitter as the now-hidden
  // generative layout keeps drifting. Reset if we scrub back before commit.
  // The commit's sentence index is captured with it, so fillSlots can DECAY that
  // favor (TT-973) instead of pinning those early sentences all meeting.
  const commitRef = useRef(null);
  if (!layout) {
    commitRef.current = null;
  } else if (curated && commitRef.current === null) {
    commitRef.current = {
      onscreen: new Set(generativePlacements.map((p) => p.sentence).filter(Boolean)),
      sent: state.playheadSent,
    };
  }

  // Signature of what fillSlots/buildCuratedComposition actually depend on, so
  // the composition is recomputed only when the symbol set, counts, or sentence
  // buffers change — not on every playhead tick (state is a fresh object each
  // tick). The commit snapshot is frozen, so it isn't part of this.
  //
  // Note this also paces dwell-driven rotation: a stretch that adds no new scored
  // content doesn't re-fill, so the scene holds until there's something new to
  // say — and the change budget, which accrues in sentences, catches up when it
  // resumes.
  const fillSignature = useMemo(() => {
    if (!curated) return null;
    let sig = state.primary || '';
    for (const symbolId of state.ranked) {
      const e = state.symbols[symbolId];
      sig += `|${symbolId}:${e.count}:${(e.sentences || []).length}`;
    }
    return sig;
  }, [curated, state]);

  // Slot→sentence assignments from the previous fill. fillSlots retains them
  // (subject to per-slot dwell) so text rotates gently instead of being
  // re-derived from scratch every tick (TT-973). Keyed by the playhead — the
  // clock dwell is measured in — so re-running the fill at the same sentence is
  // idempotent by construction, whatever made it re-run (React Strict Mode's
  // double render, a discarded concurrent render, a dropped memo entry).
  const fillMemoryRef = useRef({ at: null, input: null, output: null });

  // Curated composition: filled slots → symbols (in the layout's coordinate
  // space) + text along the layout's authored textpaths.
  const composition = useMemo(() => {
    if (!curated) return null;
    if (playback) {
      // The recorded fill, as it was: no scoring, memory or dwell.
      if (playback.phase !== 'curated') return null;
      const filled = replayFill(curatedDetail.layout, playback.slots);
      const built = buildCuratedComposition(filled, null, curatedDetail);
      built.filledSlots = filled.slots;
      return built.symbols.length || built.texts.length ? built : null;
    }
    if (timeline) {
      if (!replayed?.filled) return null;
      const built = buildCuratedComposition(replayed.filled, state, curatedDetail);
      built.filledSlots = replayed.filled.slots;
      return built.symbols.length || built.texts.length ? built : null;
    }
    if (!symbolTags || !symbolKeywords) return null;
    const mem = fillMemoryRef.current;
    const previous = mem.at === state.playheadSent ? mem.input : mem.output;
    const filled = fillSlots(curatedDetail.layout, state, state.playheadSent, {
      symbolTags,
      symbolKeywords,
      onscreenSentences: commitRef.current?.onscreen,
      onscreenCommitSent: commitRef.current?.sent,
      previous,
    });
    fillMemoryRef.current = { at: state.playheadSent, input: previous, output: filled.assignments };
    const built = buildCuratedComposition(filled, state, curatedDetail);
    // The fill behind the picture, for the frame `onPaint` reports.
    built.filledSlots = filled.slots;
    return built.symbols.length || built.texts.length ? built : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curatedDetail, curated, symbolTags, symbolKeywords, fillSignature, playback, timeline, replayed]);

  // The curated background and its textpaths take over together as soon as the
  // composition is ready: the text moves straight into the layout's positions.
  const showCurated = !!composition;

  // Curated symbols once ready; otherwise the generative packer (the "building
  // up" phase). Same symbol_id keys → persisting symbols glide via CSS transition.
  const activePlacements = showCurated
    ? composition.symbols
    : playback
      ? playback.placements || EMPTY_PLACEMENTS
      : generativePlacements;
  const placements = useExitFadePlacements(activePlacements, instant);

  // Ordered progressive-reveal pieces for the active background (empty ⇒ the
  // background is a single image and uses the classic one-shot reveal below).
  const pieces = showCurated && Array.isArray(curatedDetail.pieces) ? curatedDetail.pieces : EMPTY_PIECES;
  const hasPieces = pieces.length > 0;

  // The background this reveal sequence belongs to. Switching it restarts the
  // reveal and lets the piece counts below reset synchronously on a switch.
  const revealBgId = showCurated ? curatedDetail.id : null;

  // Publish that same background to the host (TT-972), so it can show the symbol's
  // field-guide description beside the canvas. It reads `revealBgId` rather than a
  // second notion of "active": `showCurated` is the renderer's own paint gate, so
  // the host is told exactly what is on screen and nothing while still generative.
  // Both deps are primitives, so this fires only when the background really changes.
  const revealBgSymbolId = showCurated ? curatedDetail.primarySymbolId || '' : '';
  useEffect(() => {
    onActiveBackgroundChange?.(revealBgId ? { id: revealBgId, primarySymbolId: revealBgSymbolId } : null);
  }, [revealBgId, revealBgSymbolId, onActiveBackgroundChange]);

  // Report the picture to the host when it changes (TT-1227). The frame is
  // rebuilt every tick (its `at` moves), but it is reported on a change of
  // its signature only: the packer hands back a fresh array each sentence
  // whether or not anything moved. A playback frame is not reported back.
  const frame = useMemo(
    () =>
      paintFrame({
        playheadSent: state.playheadSent,
        currentSent,
        showCurated,
        composition,
        curatedDetail,
        placements: activePlacements,
        canvasSize,
        isFullscreen,
      }),
    [state.playheadSent, currentSent, showCurated, composition, curatedDetail, activePlacements, canvasSize, isFullscreen]
  );
  const paintSig = onPaint && !playback && canvasSize.height > 0 ? paintSignature(frame) : '';
  const onPaintRef = useRef(onPaint);
  onPaintRef.current = onPaint;
  const frameRef = useRef(frame);
  frameRef.current = frame;
  useEffect(() => {
    if (paintSig) onPaintRef.current?.(frameRef.current);
  }, [paintSig]);

  // Background "floor": the image shown beneath the reveal layers so a switch
  // never exposes the bare default canvas background. Two parts, so that rapid
  // switching can't strand a background from several switches ago on screen (each
  // carries the full `imageUrl`, so this works for single + pieced):
  //   - Until the current background finishes revealing, the floor is the
  //     IMMEDIATELY-previous background (`bgPair.prev`, tracked during render), so
  //     it's already correct the frame a switch lands — no promote-timer to race,
  //     and never a stale one from several switches back.
  //   - Once the current background has fully revealed, a timer promotes IT to
  //     the floor (`settledFloorUrl`), so it stays put behind later reveals and
  //     is what image export captures.
  //
  // Pieced backgrounds intentionally use this same full-scene crossfade (unlike
  // the pre-fade renderer, which floored only single-image backgrounds): their
  // first piece is a full background fill that covers the previous background
  // almost immediately, so it reads as one crossfade rather than the old bg
  // bleeding through the still-assembling pieces. The settled floor / export use
  // the composite `imageUrl`, which equals the fully-assembled pieces.
  const fullImageUrl = showCurated && curatedDetail.imageUrl ? curatedDetail.imageUrl : null;
  const fullRevealMs = hasPieces
    ? Math.max(0, pieces.length - 1) * PIECE_INTERVAL_MS + REVEAL_DURATION_MS
    : REVEAL_DURATION_MS;
  // Track the current background image and the one shown before it, adjusted
  // DURING render (React's "store info from previous renders" pattern) so both
  // are correct the very frame a switch lands. An effect-updated ref would lag a
  // render: it collapses `prev` to the current image on the tick after a switch,
  // which would drop the floor onto the still-revealing background and defeat its
  // own reveal. Only advances when the image really changes (idempotent — the
  // guard stops the render-phase set from looping).
  const [bgPair, setBgPair] = useState({ cur: null, prev: null });
  const [settledFloorUrl, setSettledFloorUrl] = useState(null);
  if (fullImageUrl && fullImageUrl !== bgPair.cur) {
    setBgPair({ cur: fullImageUrl, prev: bgPair.cur });
    // A new reveal starts (including switching back to a previously-settled
    // background): clear the settled marker so the floor shows the PREVIOUS
    // background until THIS reveal completes, rather than snapping the returning
    // one in fully with no crossfade.
    setSettledFloorUrl(null);
  } else if (!showCurated && bgPair.cur !== null) {
    // Entering the generative (bg-less) phase clears the screen to the dark
    // canvas; forget the previous background so it doesn't flash back beneath the
    // next one when curated resumes (that reveal should fade up from dark).
    setBgPair({ cur: null, prev: null });
  }
  useEffect(() => {
    if (!showCurated || !fullImageUrl) return;
    const t = setTimeout(() => setSettledFloorUrl(fullImageUrl), fullRevealMs);
    return () => clearTimeout(t);
  }, [fullImageUrl, fullRevealMs, showCurated]);
  // Current background once it has settled (also the export base); until then the
  // previous one shows behind the reveal. Blank during the generative phase.
  const floorBgUrl = !showCurated ? null : instant || settledFloorUrl === fullImageUrl ? fullImageUrl : bgPair.prev;

  // Progressive disclosure driver: how many pieces have begun fading in (1..N).
  // The first starts immediately; the rest follow on a fixed timer — the one
  // swappable seam a future version can drive from backend drips instead. The
  // count is tagged with the background id so that on a switch it reads as 0 for
  // THIS render (before the effect restarts the sequence), and we never render
  // the incoming background's later pieces — a full-image flash — for a frame.
  const [revealState, setRevealState] = useState({ id: null, count: 0 });
  // Applies only to the background it was measured for; reads 0 for any other —
  // i.e. immediately after a switch, until the effect below restarts the sequence.
  const revealedCount = revealState.id === revealBgId ? revealState.count : 0;
  useEffect(() => {
    // Single-image backgrounds don't use this driver; revealedCount already reads
    // 0 for them via the id mismatch above, so there's nothing to set or run.
    if (!hasPieces) return;
    setRevealState({ id: revealBgId, count: 1 }); // first piece fades in right away
    if (pieces.length <= 1) return;
    let count = 1;
    const interval = setInterval(() => {
      count += 1;
      setRevealState({ id: revealBgId, count });
      if (count >= pieces.length) clearInterval(interval);
    }, PIECE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [revealBgId, hasPieces, pieces.length]);

  // Gutter reserved for host chrome while fullscreen (0 = none, the default).
  const fullscreenAsideWidth = isFullscreen && fullscreenAside ? asideWidth(windowSize.w) : 0;

  // Visual scale factor when fullscreen — fit-in-viewport preserving aspect,
  // inside whatever width the aside leaves us.
  const fullscreenScale = (() => {
    if (!isFullscreen || !canvasSize.width || !canvasSize.height || !windowSize.w) return 1;
    const availWidth = Math.max(1, windowSize.w - fullscreenAsideWidth);
    return Math.min(availWidth / canvasSize.width, windowSize.h / canvasSize.height);
  })();

  // With an aside the canvas hugs the left edge so the gutter is a single unbroken
  // column; without one it stays centered as it always has. `translate` shifts by
  // the UNSCALED size, and the matching transform-origin is what keeps the anchored
  // edge pinned under `scale`.
  const anchorLeft = fullscreenAsideWidth > 0;

  const canvasStyle = isFullscreen
    ? {
        ...surface,
        position: 'fixed',
        top: '50%',
        left: anchorLeft ? 0 : '50%',
        transform: `translate(${anchorLeft ? '0' : '-50%'}, -50%) scale(${fullscreenScale})`,
        transformOrigin: anchorLeft ? 'left center' : 'center center',
        width: `${canvasSize.width}px`,
        height: `${canvasSize.height}px`,
        // Sit above host app chrome (e.g. a fixed MUI AppBar at z-index ~1100).
        zIndex: 9999,
        overflow: 'hidden',
      }
    : {
        ...surface,
        // Structural layout is set inline (not via utility classes) so the
        // renderer positions correctly in hosts without Tailwind. The
        // `position: relative` here is what anchors the absolutely-positioned
        // symbols to the canvas frame.
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        aspectRatio: `${activeAspect}`,
      };

  // A click as a point on the frame. The rect is the frame as drawn, so the
  // fractions hold under the fullscreen transform; placements are centres and
  // sizes in canvas heights, which is what the hit test compares in. Symbols
  // take no pointer events, so the symbol under a click is found here.
  const handlePoint = (e) => {
    if (!onPoint || e.target.closest?.('button')) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect.height) return;
    const x = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const y = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
    const hx = (x * rect.width) / rect.height;
    let symbolId = null;
    let nearest = Infinity;
    for (const p of placements) {
      if (p.exiting) continue;
      const dx = Math.abs(hx - p.x);
      const dy = Math.abs(y - p.y);
      if (dx > p.size / 2 || dy > p.size / 2) continue;
      if (dx * dx + dy * dy < nearest) {
        nearest = dx * dx + dy * dy;
        symbolId = p.symbolId;
      }
    }
    onPoint({ x, y, symbolId });
  };
  const pinStyle = (pin) => {
    const on = pin.symbolId ? placements.find((p) => p.symbolId === pin.symbolId && !p.exiting) : null;
    return on
      ? { left: `${on.x * canvasSize.height}px`, top: `${on.y * canvasSize.height}px` }
      : { left: `${pin.x * 100}%`, top: `${pin.y * 100}%` };
  };

  // Publish fullscreen state so a host can drop its own inline placement of the
  // chrome it handed us for the aside. A bare boolean, so a host holding it in
  // state re-renders only on a real transition.
  useEffect(() => {
    onFullscreenChange?.(isFullscreen);
    return isFullscreen ? () => onFullscreenChange?.(false) : undefined;
  }, [isFullscreen, onFullscreenChange]);

  // Snapshot the canvas root to a PNG, exposed to the host via ref so it can
  // place the trigger in its own chrome. On-canvas controls (fullscreen button,
  // snackbar) are tagged data-export-ignore so they're dropped from the capture.
  useImperativeHandle(
    ref,
    () => ({
      exportPng: () => exportTapestryPng(canvasRef.current, { meetingName, dateStr }),
    }),
    [meetingName, dateStr]
  );

  return (
    <div className="flex flex-col gap-3" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* Full-screen backdrop — clickable to exit */}
      {isFullscreen && (
        <div
          className="fixed inset-0 bg-black/95 z-40"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 9998 }}
          onClick={() => setIsFullscreen(false)}
        />
      )}

      {/* Host chrome in the reserved gutter. A sibling of the canvas (never inside
          it, so image export can't capture it), stacked above both the canvas and
          the backdrop. Geometry is ours; the surface inside is the host's. */}
      {fullscreenAsideWidth > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: `${fullscreenAsideWidth}px`,
            display: 'flex',
            boxSizing: 'border-box',
            zIndex: 10000,
          }}
        >
          {fullscreenAside}
        </div>
      )}

      {/* Canvas — stays in the same DOM position regardless of mode.
          Structural styling lives in `canvasStyle` (inline) so it works
          without Tailwind; the border class is cosmetic. */}
      <div
        ref={canvasRef}
        className={`border border-theme-border ${isFullscreen ? '' : 'relative w-full'}${snapLayout ? ' tapestry-snap' : ''}${instant ? ' tapestry-instant' : ''}`}
        data-tapestry-instant={instant || undefined}
        style={onPoint ? { ...canvasStyle, cursor: pointCursor } : canvasStyle}
        onClick={handlePoint}
      >
        {timeline && !replayed && time != null && (
          <div role="status" className="absolute inset-0 grid place-items-center text-sm text-theme-muted">Loading scene…</div>
        )}
        {replayed?.error && (
          <div role="alert" className="absolute inset-0 grid place-items-center text-sm text-brand-err">The scene could not load: {replayed.error.message}</div>
        )}
        {/* Persistent floor: the previously fully-revealed background, held
            beneath the reveal so a switch never exposes the bare canvas
            background. Advances to the current background only once it has fully
            revealed (see floorBgUrl). Because it always holds a complete,
            settled background image, it doubles as the base that image export
            captures — the animating fade layers above are export-ignored. */}
        {showCurated && canvasSize.height > 0 && floorBgUrl && (
          // Shares FILL_LAYER_STYLE with BackgroundReveal so the floor and the
          // fading-in layers stay pixel-aligned at handoff.
          <div style={{ ...FILL_LAYER_STYLE, backgroundImage: `url(${floorBgUrl})` }} />
        )}

        {/* Curated background, faded in (over the floor) behind the symbols/text.
            Piece-based backgrounds reveal one piece at a time and accumulate;
            single-image backgrounds use the classic one-shot reveal. Keyed by
            background id so a new background re-fades. */}
        {showCurated && canvasSize.height > 0 && hasPieces && (
          <>
            {/* Every started piece is its own faded-in layer. CSS
                animation-fill-mode holds finished pieces in place while later
                ones are still fading, so reveals can overlap. */}
            {pieces.slice(0, instant ? pieces.length : revealedCount).map((piece) => (
              <BackgroundReveal key={`${curatedDetail.id}:${piece.seq}`} imageUrl={piece.url} />
            ))}
          </>
        )}
        {showCurated && canvasSize.height > 0 && !hasPieces && curatedDetail.imageUrl && (
          <BackgroundReveal key={curatedDetail.id} imageUrl={curatedDetail.imageUrl} />
        )}

        {canvasSize.height > 0 && (
          <TapestrySkinProvider value={skinCfg}>
            <SeenRevealProvider currentSent={currentSent} instant={instant}>
              <TapestryStaggerProvider>
                {showCurated ? (
                  <CuratedText
                    texts={composition.texts}
                    viewBox={composition.viewBox}
                    canvasWidth={canvasSize.width}
                    canvasHeight={canvasSize.height}
                    sceneId={curatedDetail.id}
                  />
                ) : (
                  <TapestryExternal
                    placements={placements}
                    canvasWidth={canvasSize.width}
                    canvasHeight={canvasSize.height}
                    currentSent={currentSent}
                  />
                )}
                {placements.map((p) => (
                <TapestrySymbol key={p.symbolId} placement={p} canvasHeight={canvasSize.height} imageClient={imageClient} />
                ))}
              </TapestryStaggerProvider>
            </SeenRevealProvider>
          </TapestrySkinProvider>
        )}

        {/* Title + date. Generative phase: top-left corner. Curated phase:
            positioned at the layout's title coordinates and colored from the
            background's primary color (matching the static renderer). Both
            scale off canvas size so they ride the fullscreen transform. */}
        {canvasSize.height > 0 && (meetingName || dateStr) && (
          <div
            style={
              showCurated && composition.title
                ? {
                    position: 'absolute',
                    left: `${composition.title.x * 0.96 * canvasSize.width}px`,
                    top: `${composition.title.y * canvasSize.height}px`,
                    transform: 'translate(-50%, -50%)',
                    // centred on its x, so it may span twice the nearer edge's distance
                    maxWidth: `${2 * Math.min(composition.title.x * 0.96, 1 - composition.title.x * 0.96) * canvasSize.width}px`,
                    width: 'max-content',
                    overflowWrap: 'anywhere',
                    textAlign: 'center',
                    fontFamily: `'${composition.fontFamily || 'Hand1-Regular'}', sans-serif`,
                    color: curatedDetail.colors?.primary || '#ffffff',
                    textShadow: '0 0 4px rgba(0,0,0,0.25)',
                    lineHeight: 1.15,
                    pointerEvents: 'none',
                  }
                : {
                    position: 'absolute',
                    top: `${canvasSize.height * 0.035}px`,
                    left: `${canvasSize.height * 0.04}px`,
                    fontFamily: `'${skinCfg.font}', sans-serif`,
                    color: skinCfg.title,
                    textShadow: skinCfg.titleShadow,
                    lineHeight: 1.15,
                    pointerEvents: 'none',
                  }
            }
          >
            {showCurated && composition.title ? (
              // Curated: title and date on one line (date appended after a space),
              // matching the static tapestry title.
              <div style={{ fontSize: `${canvasSize.height * 0.042}px` }}>
                {[meetingName, showDate ? dateStr : null].filter(Boolean).join(' ')}
              </div>
            ) : (
              <>
                {meetingName && <div style={{ fontSize: `${canvasSize.height * 0.042}px` }}>{meetingName}</div>}
                {showDate && (
                  <div
                    style={{
                      fontSize: `${canvasSize.height * 0.028}px`,
                      opacity: 0.85,
                    }}
                  >
                    {dateStr}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Host pins, over everything drawn and under the controls. Inside the
            frame so they ride the fullscreen transform; out of every export. */}
        {canvasSize.height > 0 && pins?.length > 0 && (
          <div data-export-ignore style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {pins.map((pin) => (
              <button
                key={pin.id}
                type="button"
                title={pin.label}
                aria-label={pin.label || 'Pin'}
                onClick={() => onPinClick?.(pin.id)}
                className={`tapestry-pin${pin.tone ? ` tapestry-pin-${pin.tone}` : ''}`}
                style={pinStyle(pin)}
              />
            ))}
          </div>
        )}

        {/* Fullscreen toggle button — top right of the canvas */}
        {fullscreenControl && (
          <button
            data-export-ignore
            onClick={(e) => {
              e.stopPropagation();
              setIsFullscreen((v) => !v);
            }}
            // the chrome is the .tapestry-fs-btn rule (styles/tapestryAnimations.css)
            className="tapestry-fs-btn backdrop-blur-sm"
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M8 3v4a1 1 0 0 1-1 1H3" />
                <path d="M21 8h-4a1 1 0 0 1-1-1V3" />
                <path d="M3 16h4a1 1 0 0 1 1 1v4" />
                <path d="M16 21v-4a1 1 0 0 1 1-1h4" />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 7V3h4" />
                <path d="M21 7V3h-4" />
                <path d="M3 17v4h4" />
                <path d="M21 17v4h-4" />
              </svg>
            )}
          </button>
        )}

        <div data-export-ignore>
          <LayoutSnackbar
            backgroundId={!forcedBackgroundId && layout?.primaryMatch ? layout.backgroundId : null}
            symbolName={!forcedBackgroundId && layout?.primaryMatch ? nameOf(layout.matchedSymbolId) : null}
          />
        </div>
      </div>

      {/* Debug toggle — hidden when the host disables debug tools */}
      {debugTools && (
        <button
          onClick={() => setShowDebug((v) => !v)}
          className="self-start text-xs text-theme-muted hover:text-theme-primary transition-colors cursor-pointer"
        >
          {showDebug ? '▾' : '▸'} debug ({ranked.length} symbols · {events.length} events)
        </button>
      )}

      {debugTools && showDebug && (
        <>
          {/* Primary badge */}
          <div className="rounded-lg border border-theme-border bg-theme-secondary p-3">
            <div className="text-xs uppercase tracking-wide text-theme-muted mb-1">Primary</div>
            <div className="text-xl font-semibold text-theme-accent-primary">
              {primary ? nameOf(primary) : <span className="text-theme-muted italic">—</span>}
            </div>
          </div>

          {/* Committed layout/background (curated selection after hysteresis) */}
          <div className="rounded-lg border border-theme-border bg-theme-secondary p-3">
            <div className="text-xs uppercase tracking-wide text-theme-muted mb-1">
              Layout {layoutCatalog ? `(${layoutCatalog.backgrounds.length} eligible)` : '(catalog off)'}
            </div>
            {layout ? (
              <div className="text-sm font-mono text-sky-400">
                bg {layout.backgroundId} · style {layout.style || '—'} ·{' '}
                {showCurated
                  ? `${composition.symbols.length} symbols, ${composition.texts.length} texts`
                  : 'loading detail…'}
              </div>
            ) : (
              <div className="text-sm text-theme-muted italic">— not yet committed</div>
            )}
          </div>

          {/* Role roster */}
          <div className="rounded-lg border border-theme-border bg-theme-secondary p-3">
            <div className="text-xs uppercase tracking-wide text-theme-muted mb-2">Roster ({ranked.length})</div>
            {ranked.length === 0 ? (
              <div className="text-sm text-theme-muted italic">No symbols yet</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {ranked.map((symbolId) => {
                  const entry = symbols[symbolId];
                  const role = entry?.role || 'hidden';
                  return (
                    <span
                      key={symbolId}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded border ${ROLE_STYLES[role]}`}
                      title={`${symbolId} · role: ${role} · count: ${entry?.count || 0}`}
                    >
                      <span>{nameOf(symbolId)}</span>
                      <span className="text-[10px] opacity-60">{entry?.count || 0}</span>
                      <span className="text-[10px] uppercase opacity-50">{role[0]}</span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step events */}
          <div className="rounded-lg border border-theme-border bg-theme-secondary p-3">
            <div className="text-xs uppercase tracking-wide text-theme-muted mb-2">
              This step ({events.length} events)
            </div>
            {events.length === 0 ? (
              <div className="text-sm text-theme-muted italic">No events</div>
            ) : (
              <div className="space-y-1 font-mono text-xs">
                {events.map((ev, i) => (
                  <div key={i} className={EVENT_STYLES[ev.type] || 'text-theme-secondary'}>
                    <span className="opacity-60">{ev.type.padEnd(17)}</span>
                    {ev.type === 'symbol.introduce' && ` ${nameOf(ev.symbolId)} (count: ${ev.count})`}
                    {ev.type === 'symbol.reinforce' && ` ${nameOf(ev.symbolId)} +${ev.delta} → ${ev.count}`}
                    {ev.type === 'sentence.surface' && (
                      <>
                        {' '}
                        <span className="text-theme-accent-primary">{ev.score.toFixed(2)}</span>{' '}
                        <span className="truncate inline-block max-w-[70%] align-bottom">{ev.text}</span>
                      </>
                    )}
                    {ev.type === 'primary.change' && (
                      <>
                        {' '}
                        {ev.from ? nameOf(ev.from) : '∅'} → {nameOf(ev.to)}
                      </>
                    )}
                    {ev.type === 'layout.change' && (
                      <>
                        {' '}
                        {ev.from || '∅'} → {ev.to} <span className="opacity-60">[{ev.style || '—'}]</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default forwardRef(TapestryCanvas);
